import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Snowflake, User, Building2, Mail, ArrowRight, Check } from "lucide-react";

const BULLETS = [
  "Corso di preparazione ed esame teorico-pratico",
  "Certificazione per le persone e per le imprese",
  "Pratiche e iscrizione al Registro telematico nazionale FGAS",
  "Aggiornamenti e rinnovi alla scadenza",
];

const CARDS = [
  {
    icon: User,
    title: "Per le persone",
    body: "Tecnici e frigoristi: abilitazione individuale per installare, manutenere e riparare apparecchiature con gas fluorurati, con corso ed esame in un'unica sede.",
  },
  {
    icon: Building2,
    title: "Per le imprese",
    body: "Aziende della refrigerazione e della climatizzazione: certificazione aziendale obbligatoria per operare sugli impianti, con supporto documentale completo.",
  },
];

export const Fgas = () => {
  return (
    <section data-testid="fgas-section" className="bg-brand-black text-white border-b border-border">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left — message */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-5">
              <Snowflake size={16} /> Certificazione FGAS · Gas fluorurati
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter leading-[0.98] text-white">
              Gas fluorurati: in regola, persone e imprese.
            </h2>
            <p className="mt-6 text-white/80 text-sm md:text-base leading-relaxed max-w-2xl">
              La certificazione FGAS è obbligatoria per chi installa, mantiene, ripara o smaltisce
              condizionatori, pompe di calore e impianti di refrigerazione contenenti gas fluorurati.
              Ti seguiamo in tutto il percorso: preparazione, esame e pratiche, fino all'iscrizione
              al Registro telematico nazionale FGAS.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/85">
                  <span className="mt-0.5 w-5 h-5 shrink-0 bg-brand-red flex items-center justify-center">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contatti#contatti"
                data-testid="fgas-form-cta"
                className="inline-flex items-center justify-center gap-2 bg-brand-red text-white text-sm font-bold uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-brand-black transition-colors"
              >
                <Mail size={18} /> Richiedi info FGAS
              </Link>
              <Link
                to="/fgas"
                data-testid="fgas-page-link"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white text-sm font-bold uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-brand-black transition-colors"
              >
                Scopri il percorso FGAS <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>

          {/* Right — two paths */}
          <div className="lg:col-span-5 grid gap-4 md:gap-5">
            {CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.article
                  key={c.title}
                  data-testid={`fgas-card-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                  className="group bg-white/5 border border-white/15 p-6 md:p-7 hover:border-brand-red transition-colors"
                >
                  <div className="w-12 h-12 mb-5 flex items-center justify-center bg-brand-red text-white">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-white leading-tight mb-3">
                    {c.title}
                  </h3>
                  <p className="text-white/75 text-sm md:text-base leading-relaxed">{c.body}</p>
                </motion.article>
              );
            })}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="fgas-normativa"
              className="text-[11px] uppercase tracking-[0.18em] text-white/50 leading-relaxed"
            >
              Riferimenti: Reg. (UE) 2024/573 · D.P.R. 146/2018 · Registro telematico nazionale FGAS
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};
